import type { StoredMessage } from '../lib/api';

interface TextPart {
  type?: 'text';
  text: string;
}
interface ToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  input: unknown;
}
interface ToolResultPart {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  output: { type: string; value: unknown };
}
type Part = TextPart | ToolCallPart | ToolResultPart | { type: string; [k: string]: unknown };

const parts = (m: StoredMessage): Part[] => {
  const c = m.content.content;
  if (typeof c === 'string') return [{ type: 'text', text: c }];
  return Array.isArray(c) ? (c as Part[]) : [];
};
const isText = (p: Part): p is TextPart => typeof (p as TextPart).text === 'string';
const isCall = (p: Part): p is ToolCallPart => p.type === 'tool-call';
const isResult = (p: Part): p is ToolResultPart => p.type === 'tool-result';

/** A D6 denial as the worker reports it back to the model. */
interface Denied {
  denied: true;
  connector: string;
  tool: string;
  reason?: string;
}
const asDenied = (v: unknown): Denied | null => {
  const d = v as Partial<Denied> | null;
  return d && d.denied === true && typeof d.connector === 'string' && typeof d.tool === 'string'
    ? (d as Denied)
    : null;
};

/** Index tool calls by id so a result (and a denial) can show the arguments that caused it. */
const callIndex = (messages: StoredMessage[]) => {
  const m = new Map<string, ToolCallPart>();
  for (const msg of messages) for (const p of parts(msg)) if (isCall(p)) m.set(p.toolCallId, p);
  return m;
};

export function MessageList({
  threadId,
  messages,
}: {
  threadId: string;
  messages: StoredMessage[];
}) {
  const calls = callIndex(messages);
  return (
    <>
      {messages.map((m) => {
        if (m.role === 'system') return null;
        if (m.role === 'user') {
          const c = m.content.content;
          return (
            <div className="msg user" key={m.id}>
              {typeof c === 'string' ? c : JSON.stringify(c)}
            </div>
          );
        }
        return (
          <div className="stack" key={m.id}>
            {parts(m).map((p, i) => {
              if (isText(p))
                return p.text ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: parts of a persisted message never reorder
                  <div className="msg assistant" key={i}>
                    {p.text}
                  </div>
                ) : null;
              if (isCall(p)) return <ToolCall part={p} key={p.toolCallId} />;
              if (isResult(p)) {
                const denied = asDenied(p.output?.value);
                const call = calls.get(p.toolCallId);
                if (denied)
                  return (
                    <DecisionNote
                      key={p.toolCallId}
                      connector={denied.connector}
                      tool={denied.tool}
                      reason={denied.reason}
                      args={(call?.input as { args?: unknown } | undefined)?.args}
                    />
                  );
                return <ToolResult part={p} key={p.toolCallId} />;
              }
              return null;
            })}
          </div>
        );
      })}
    </>
  );
}

function ToolCall({ part }: { part: ToolCallPart }) {
  return (
    <details className="tool">
      <summary>
        ▸ <code>{part.toolName}</code>
      </summary>
      <pre>{JSON.stringify(part.input, null, 2)}</pre>
    </details>
  );
}

function ToolResult({ part }: { part: ToolResultPart }) {
  const v = part.output?.value;
  const text = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
  return (
    <details className="tool">
      <summary>
        ✓ <code>{part.toolName}</code> result
      </summary>
      <pre>{text}</pre>
    </details>
  );
}

/** A gated call the user denied (or that timed out): shown inert, with the arguments it would have used. */
function DecisionNote({
  connector,
  tool,
  reason,
  args,
}: {
  connector: string;
  tool: string;
  reason?: string | undefined;
  args: unknown;
}) {
  return (
    <div className="tool">
      <div>
        ✕{' '}
        <code>
          {connector}.{tool}
        </code>{' '}
        not executed{reason ? ` — ${reason}` : ''}
      </div>
      {args !== undefined && <pre>{JSON.stringify(args, null, 2)}</pre>}
    </div>
  );
}
